/**
 * Remove Player Section
 * Lets the teacher remove any human or bot player from the game.
 * Two-step flow: pick player → confirm → leaveGame mutation.
 */

import { View, Text, Pressable, TouchableOpacity } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useState, useCallback } from 'react'
import { Popover } from '../Popover'
import { teacherControlStyles as styles } from './styles'
import type { PlayerInfo } from './types'
import { translate } from '../../../i18n'

export interface RemovePlayerSectionProps {
  isOpen: boolean
  onClose: () => void
  onOpen: () => void
  players: PlayerInfo[]
  onRemovePlayer: (player: PlayerInfo) => void
}

export function RemovePlayerSection({
  isOpen,
  onClose,
  onOpen,
  players,
  onRemovePlayer
}: RemovePlayerSectionProps) {
  const [pendingPlayer, setPendingPlayer] = useState<PlayerInfo | null>(null)

  const handleClose = useCallback(() => {
    setPendingPlayer(null)
    onClose()
  }, [onClose])

  const handleBack = useCallback(() => {
    if (pendingPlayer) {
      setPendingPlayer(null)
    } else {
      handleClose()
    }
  }, [pendingPlayer, handleClose])

  const handleConfirm = useCallback(() => {
    if (!pendingPlayer) return
    onRemovePlayer(pendingPlayer)
    setPendingPlayer(null)
    onClose()
  }, [pendingPlayer, onRemovePlayer, onClose])

  const triggerButton = (
    <View style={[styles.button, styles.removePlayerButton]}>
      <Text style={styles.buttonText}>{translate('teacherControls.removePlayer.title')}</Text>
    </View>
  )

  const popoverContent = pendingPlayer ? (
    // Step 2: Confirm
    <View>
      <View style={styles.popoverHeader}>
        <TouchableOpacity onPress={handleBack} hitSlop={8}>
          <Text style={styles.backArrow}>←</Text>
        </TouchableOpacity>
        <Text style={styles.popoverTitle}>{translate('teacherControls.removePlayer.title')}</Text>
      </View>
      <Text style={styles.confirmText}>
        {translate('teacherControls.removePlayer.confirmMessage', {
          name: `${pendingPlayer.kind === 'bot' ? '🤖 ' : '👤 '}${pendingPlayer.name}`
        })}
      </Text>
      <Text style={styles.confirmHint}>{translate('teacherControls.removePlayer.confirmHint')}</Text>
      <View style={[styles.stepperActions, { marginTop: 12 }]}>
        <Pressable
          onPress={handleBack}
          style={({ pressed }) => [styles.smallButton, styles.cancelButton, pressed && { opacity: 0.7 }]}
        >
          <Text style={styles.smallButtonText}>{translate('common.cancel')}</Text>
        </Pressable>
        <Pressable
          onPress={handleConfirm}
          style={({ pressed }) => [styles.smallButton, styles.removeConfirmButton, pressed && { opacity: 0.7 }]}
        >
          <Text style={[styles.smallButtonText, styles.removeConfirmButtonText]}>
            {translate('teacherControls.removePlayer.remove')}
          </Text>
        </Pressable>
      </View>
    </View>
  ) : (
    // Step 1: Player list
    <View>
      <View style={styles.popoverHeader}>
        <TouchableOpacity onPress={handleClose} hitSlop={8}>
          <Text style={styles.backArrow}>←</Text>
        </TouchableOpacity>
        <Text style={styles.popoverTitle}>{translate('teacherControls.removePlayer.title')}</Text>
      </View>
      <ScrollView style={styles.recipientList} showsVerticalScrollIndicator={false}>
        {players.map((player) => (
          <Pressable
            key={player._id}
            onPress={() => setPendingPlayer(player)}
            style={({ pressed }) => [styles.recipientItem, pressed && { opacity: 0.7 }]}
          >
            <Text style={styles.recipientIcon}>{player.kind === 'bot' ? '🤖' : '👤'}</Text>
            <View style={styles.recipientNameRow}>
              <Text style={styles.recipientName} numberOfLines={1}>
                {player.name}
              </Text>
              <Text style={styles.recipientChips}>{player.chips.toLocaleString()}</Text>
            </View>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  )

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      onTriggerPress={onOpen}
      trigger={triggerButton}
      side="top"
      align="end"
      sideOffset={12}
      contentStyle={styles.popoverContent}
    >
      {popoverContent}
    </Popover>
  )
}
